import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, {
  clearAccessToken,
  clearSessionHints,
  hasSessionHint,
  setAccessToken,
  setSessionHint,
} from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }
    try {
      const refresh = await api.post('/auth/refresh');
      setAccessToken(refresh.data.accessToken);
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      clearAccessToken();
      clearSessionHints();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password, { remember = true } = {}) => {
    clearAccessToken();
    clearSessionHints();
    setUser(null);
    try { await api.post('/auth/logout'); } catch { /* clear stale cookie best effort */ }
    const { data } = await api.post('/auth/login', { email, password, remember });
    setAccessToken(data.accessToken);
    setSessionHint(remember);
    const me = await api.get('/auth/me');
    const resolvedUser = me.data.user || data.user;
    if (String(resolvedUser?.email || '').toLowerCase() !== String(email || '').toLowerCase()) {
      clearAccessToken();
      clearSessionHints();
      setUser(null);
      throw new Error('Authenticated session does not match the requested user.');
    }
    setUser(resolvedUser);
    return resolvedUser;
  };

  const register = async ({ name, email, phone, password, region, plan, language, acceptedTerms, termsVersion }) => {
    const { data } = await api.post('/auth/register', { name, email, phone, password, region, plan, language, acceptedTerms, termsVersion });
    setAccessToken(data.accessToken);
    setSessionHint(true);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearAccessToken();
    clearSessionHints();
    setUser(null);
  };

  const updateUser = (updatedUser) => setUser(updatedUser);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, refreshUser: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
